import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { removeSupabaseChannelByName, subscribePostgresChannel } from "@/lib/realtime-channel";
import { reactivateConversationAgent } from "@/lib/reactivate-conversation-agent";
import {
  fetchPendingEscalationRestoreTarget,
  restoreLeadStageAfterEscalation,
  type ResolveEscalationResult,
} from "@/lib/resolve-escalation-followup";

export type ResolveLeadEscalationsOptions = {
  reactivateAgent?: boolean;
  conversationId?: string | null;
  memberId?: string | null;
  workspaceId?: string | null;
  integrationId?: string | null;
};

export type { ResolveEscalationResult } from "@/lib/resolve-escalation-followup";

export interface UseLeadEscalationsResult {
  pendingCount: number;
  totalCount: number;
  loading: boolean;
  resolveAll: (options?: ResolveLeadEscalationsOptions) => Promise<ResolveEscalationResult>;
  refresh: () => Promise<void>;
}

/**
 * Tracks escalation events for a lead (pending + total) with realtime updates.
 * Used by the chat header to show a "Resolver escalação" button when pending > 0.
 */
export function useLeadEscalations(leadId: string | null | undefined): UseLeadEscalationsResult {
  const [pendingCount, setPendingCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    if (!leadId) {
      setPendingCount(0);
      setTotalCount(0);
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from("lead_escalation_events")
        .select("id, resolved_at")
        .eq("lead_id", leadId);
      if (error) throw error;
      const total = data?.length || 0;
      const pending = (data || []).filter((e) => !e.resolved_at).length;
      setTotalCount(total);
      setPendingCount(pending);
    } catch (err) {
      console.error("[useLeadEscalations] fetch error:", err);
    } finally {
      setLoading(false);
    }
  }, [leadId]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  // Realtime: refresh on insert/update for this lead
  useEffect(() => {
    if (!leadId) return;
    const channelName = `lead-escalations-${leadId}`;
    const channel = subscribePostgresChannel(
      channelName,
      {
        event: "*",
        schema: "public",
        table: "lead_escalation_events",
        filter: `lead_id=eq.${leadId}`,
      },
      () => {
        refresh();
      },
    );
    return () => {
      removeSupabaseChannelByName(channelName);
      supabase.removeChannel(channel);
    };
  }, [leadId, refresh]);

  const resolveAll = useCallback(
    async (options?: ResolveLeadEscalationsOptions) => {
      if (!leadId) return { ok: false, resolved: 0, error: "no lead" };
      try {
        const restoreTarget = await fetchPendingEscalationRestoreTarget(leadId);

        const { data, error } = await supabase.rpc("resolve_lead_escalations", {
          p_lead_id: leadId,
        });
        if (error) throw error;

        const row = Array.isArray(data) ? data[0] : data;
        const resolved = Number((row as { resolved_events?: number })?.resolved_events ?? 0);

        let stageRestored = false;
        let restoredStageSlug: string | undefined;
        if (options?.workspaceId) {
          const restore = await restoreLeadStageAfterEscalation(leadId, options.workspaceId, {
            preferIntegrationId: options.integrationId,
            restoreTarget,
          });
          stageRestored = restore.restored;
          restoredStageSlug = restore.stageSlug;
        }

        let agentReactivated = false;
        let agentBlocked = false;
        if (options?.reactivateAgent && options.conversationId) {
          const resume = await reactivateConversationAgent({
            conversationId: options.conversationId,
            leadId,
            memberId: options.memberId ?? null,
            workspaceId: options.workspaceId ?? null,
          });
          if (resume.ok === true) {
            agentReactivated = true;
          } else if (resume.blocked === true) {
            agentBlocked = true;
          } else {
            await refresh();
            return {
              ok: true,
              resolved,
              stageRestored,
              restoredStageSlug,
              agentReactivated: false,
              agentError: resume.error,
            };
          }
        }

        await refresh();
        return { ok: true, resolved, stageRestored, restoredStageSlug, agentReactivated, agentBlocked };
      } catch (err: unknown) {
        console.error("[useLeadEscalations] resolveAll error:", err);
        const message = err instanceof Error ? err.message : "Erro ao resolver escalação";
        return { ok: false, resolved: 0, error: message };
      }
    },
    [leadId, refresh],
  );

  return { pendingCount, totalCount, loading, resolveAll, refresh };
}
